import { type ChangeEvent, useState } from 'react'
import { useRevalidator } from 'react-router'
import { ApiRequestError, api, type SessionResponse } from '../api.ts'
import { posthogCapture } from '../lib/posthog.ts'

export interface AccountSwitcherProps {
  session: SessionResponse
}

// Renders nothing for the common case (0 or 1 linked accounts) — only shows once there's
// actually something to switch between.
export function AccountSwitcher({ session }: AccountSwitcherProps) {
  const revalidator = useRevalidator()
  const [error, setError] = useState<string | null>(null)

  if (session.accounts.length <= 1) return null

  async function handleChange(event: ChangeEvent<HTMLSelectElement>) {
    setError(null)
    try {
      await api.post(`/api/accounts/${event.target.value}/activate`)
      posthogCapture('account_switched')
      // Re-runs the root loader (GET /api/session) so every page re-resolves against the
      // newly active account without a full page reload.
      revalidator.revalidate()
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.body.error.message : 'Failed to switch accounts')
      // The <select>'s value is driven entirely by session.accountId (loader data), not local
      // state, so revalidating here re-syncs it back to whatever account is actually active
      // server-side rather than leaving it showing the failed selection.
      revalidator.revalidate()
    }
  }

  return (
    <div className="flex w-full flex-col gap-0.5">
      <select
        value={session.accountId ?? ''}
        onChange={handleChange}
        aria-label="Active TikTok account"
        className="w-full truncate rounded-none border border-border-hairline-strong bg-ground-canvas px-1.5 py-1 text-xs text-ink-primary transition-colors focus:border-signal-oxide"
      >
        {session.accounts.map((account) => (
          <option key={account.id} value={account.id}>
            {account.displayName}
          </option>
        ))}
      </select>
      {error ? <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-signal-fail">{error}</span> : null}
    </div>
  )
}
